import { Fragment, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import Logo from "../../assets/images/logo.svg";
import {
  XIcon,
  MenuAlt2Icon,
  LogoutIcon,
  ViewListIcon,
  DocumentSearchIcon,
  ClipboardCheckIcon,
  UserCircleIcon,
} from "@heroicons/react/outline";
import { LoginContext } from "../../contexts/LoginContext";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function StudentSidebar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [navigation, setNavigation] = useState([
    {
      name: "All Projects",
      pathname: "/student/all-projects",
      selected: false,
      icon: DocumentSearchIcon,
    },
    {
      name: "Ongoing Projects",
      pathname: "/student/ongoing-projects",
      selected: false,
      icon: ViewListIcon,
    },
    {
      name: "Completed Projects",
      pathname: "/student/completed-projects",
      selected: false,
      icon: ClipboardCheckIcon,
    },
    {
      name: "Profile Details",
      pathname: "/student/profile-details",
      selected: false,
      icon: UserCircleIcon,
    },
  ]);

  const { handleUserLogout } = useContext(LoginContext);

  const navigate = useNavigate();

  const handleNavigation = (pathname) => {
    setNavigation((previousState) => {
      return previousState.map((tab) => {
        if (tab.pathname === pathname) {
          tab.selected = true;
        } else {
          tab.selected = false;
        }

        return tab;
      });
    });

    navigate(pathname);
  };

  useEffect(() => {
    handleNavigation(window.location.pathname);
  }, []);

  return (
    <>
      <div className="flex">
        {/* Narrow sidebar */}
        <div className="hidden w-28 bg-[#5C5CC0] overflow-y-auto sm:block">
          <div className="w-full py-6 flex flex-col items-center">
            <button
              onClick={() => handleNavigation(navigation[0].pathname)}
              className="flex items-center flex-shrink-0"
            >
              <img className="h-auto w-20 ml-3" src={Logo} alt="Workflow" />
            </button>
            <div className="flex-1 mt-6 w-full px-2 space-y-1 text-center">
              {navigation.map((item) => (
                <button
                  key={item.name}
                  type="button"
                  onClick={() => handleNavigation(item.pathname)}
                  className={classNames(
                    item.selected
                      ? "bg-indigo-800 text-white"
                      : "text-indigo-100 hover:bg-indigo-800 hover:text-white",
                    "group w-full p-3 rounded-md flex flex-col items-center text-xs font-medium"
                  )}
                >
                  <item.icon
                    className={classNames(
                      item.selected
                        ? "text-white"
                        : "text-indigo-100 group-hover:text-white",
                      "h-6 w-6"
                    )}
                    aria-hidden="true"
                  />
                  <span className="mt-2">{item.name}</span>
                </button>
              ))}
            </div>
            <div className="absolute bottom-1 w-24">
              <button
                type="button"
                onClick={handleUserLogout}
                className={classNames(
                  "bottom-0 text-indigo-100 hover:bg-indigo-800 hover:text-white group w-full p-3 rounded-md flex flex-col items-center text-xs font-medium"
                )}
              >
                <LogoutIcon
                  className={classNames(
                    "text-white group-hover:text-white",
                    "h-6 w-6"
                  )}
                  aria-hidden="true"
                />
                <span className="mt-2">Logout</span>
              </button>
            </div>
          </div>
        </div>
        {/* Mobile menu */}
        <Transition.Root show={mobileMenuOpen} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 z-40 flex sm:hidden"
            onClose={setMobileMenuOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <div className="relative max-w-xs w-full bg-indigo-700 pt-5 pb-4 flex-1 flex flex-col">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-1 right-0 -mr-14 p-1">
                    <button
                      type="button"
                      className="h-12 w-12 rounded-full flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <XIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                      <span className="sr-only">Close sidebar</span>
                    </button>
                  </div>
                </Transition.Child>
                <button
                  onClick={() => handleNavigation(navigation[0].pathname)}
                  className="flex flex-shrink-0 px-4"
                >
                  <img className="h-auto w-auto" src={Logo} alt="Workflow" />
                </button>
                <div className="mt-5 flex-1 h-0 px-2 overflow-y-auto">
                  <nav className="h-full flex flex-col">
                    <div className="space-y-1">
                      {navigation.map((item) => (
                        <button
                          key={item.name}
                          type="button"
                          onClick={() => handleNavigation(item.pathname)}
                          className={classNames(
                            item.selected
                              ? "bg-indigo-800 text-white"
                              : "text-indigo-100 hover:bg-indigo-800 hover:text-white",
                            "w-full group py-2 px-3 rounded-md flex items-center text-sm font-medium"
                          )}
                          aria-current={item.selected ? "page" : undefined}
                        >
                          <item.icon
                            className={classNames(
                              item.selected
                                ? "text-white"
                                : "text-indigo-300 group-hover:text-white",
                              "mr-3 h-6 w-6"
                            )}
                            aria-hidden="true"
                          />
                          <span>{item.name}</span>
                        </button>
                      ))}
                    </div>
                    <div className="absolute bottom-2">
                      <button
                        type="button"
                        onClick={handleUserLogout}
                        className={classNames(
                          "w-[95%] py-2 px-3 bottom-0 text-indigo-100 hover:bg-indigo-800 hover:text-white group p-3 rounded-md flex flex-col items-center text-xs font-medium"
                        )}
                      >
                        <LogoutIcon
                          className={classNames(
                            "text-white hover:text-white",
                            "h-6 w-6"
                          )}
                          aria-hidden="true"
                        />
                        <span>Logout</span>
                      </button>
                    </div>
                  </nav>
                </div>
              </div>
            </Transition.Child>
            <div className="flex-shrink-0 w-14" aria-hidden="true">
              {/* Dummy element to force sidebar to shrink to fit close icon */}
            </div>
          </Dialog>
        </Transition.Root>
        {/* Content area */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <button
            type="button"
            className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:hidden"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <MenuAlt2Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </div>
    </>
  );
}

export default StudentSidebar;
